import { memo, useContext, useMemo } from 'react';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';
import { T } from '../../../translation/src';
import { StaticColorProperty } from './ColorProperties';
import { PropertiesSection, Property } from './PropertiesSection';
import { useMeasurements } from '../../../hooks/useMeasurements';
import { ICadPolygon } from '../../../utils/CADFileParser';
import { RendererContext } from '../../../contexts/RendererContext';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { AnnotationType } from '../../Properties/types';
import { getAreaPointsProperty } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';

interface AreaPropertiesProps {
  projectId: string;
  identifier: string;
}
const AreaProperties_: React.FC2<AreaPropertiesProps> = ({ projectId, identifier }) => {
  const { formatNumber } = useFormatNumber();
  const { perimeterMeasurement } = useMeasurements();

  const renderContext = useContext(RendererContext);
  const viewer = renderContext.viewer;
  const polygonObject = viewer?.scene.measurements.find((m) => m.identifier === identifier);
  const area = polygonObject?.getArea() || 0;
  const planar = polygonObject?.isPlanar() || false;

  const points = usePropertyValue(getAreaPointsProperty(identifier));
  const perimeter = useMemo(() => {
    return perimeterMeasurement(points.map((p) => p.position));
  }, [points, perimeterMeasurement]);

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={planar ? <T _str="area (3D)" swc /> : <T _str="area (2D)" swc />}>{formatNumber(area, { convertType: ConvertType.AREA, addUnit: true })}</Property>
        <Property title={<T _str="perimeter" swc />}>{formatNumber(perimeter, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertyPanel projectId={projectId} annotationTypes={[AnnotationType.AREA]} objectIdentifiers={[identifier]} />
    </div>
  );
};
export const AreaProperties = memo(AreaProperties_);

const CadAreaProperties_: React.FC2<{ cadPolygon: ICadPolygon }> = ({ cadPolygon }) => {
  const { formatNumber } = useFormatNumber();
  const { perimeterMeasurement } = useMeasurements();

  const renderContext = useContext(RendererContext);
  const viewer = renderContext.viewer;
  const polygonObject = viewer?.scene.measurements.find((m) => m.identifier === cadPolygon.identifier);
  const area = polygonObject?.getArea() || 0;
  const planar = polygonObject?.isPlanar() || false;

  const perimeter = useMemo(() => {
    return perimeterMeasurement(cadPolygon.points);
  }, [cadPolygon.points, perimeterMeasurement]);

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          <T _str="polygon" swc />
        </Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={planar ? <T _str="area (3D)" swc /> : <T _str="area (2D)" swc />}>{formatNumber(area, { convertType: ConvertType.AREA, addUnit: true })}</Property>
        <Property title={<T _str="perimeter" swc />}>{formatNumber(perimeter, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="appearance" swc />}>
        <StaticColorProperty color={cadPolygon.color} />
      </PropertiesSection>
    </div>
  );
};

export const CadAreaProperties = memo(CadAreaProperties_);
